import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  Tooltip,
  CircularProgress
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragHandle as DragIcon,
  Publish as PublishIcon,
  Unpublished as UnPublishedIcon
} from '@mui/icons-material'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import api from '../services/api'
import AdminPageWrapper from '../components/AdminPageWrapper'

const AdminKnowledgePage = () => {
  const [knowledgeItems, setKnowledgeItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedAgent, setSelectedAgent] = useState('')
  const [publishedFilter, setPublishedFilter] = useState('all')
  const [agents, setAgents] = useState([])
  const [editDialog, setEditDialog] = useState({ open: false, item: null })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null })

  // Fetch agents list
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await api.get('/agents/?minimal=true')
        const data = response.data
        setAgents(data.agents || [])
        if (data.agents && data.agents.length > 0) {
          setSelectedAgent(data.agents[0].name)
        }
      } catch (error) {
        console.error('Error fetching agents:', error)
      }
    }
    
    fetchAgents()
  }, [])

  // Fetch knowledge items
  const fetchKnowledge = async () => {
    if (!selectedAgent) return
    
    setLoading(true)
    try {
      const params = new URLSearchParams({
        agent_name: selectedAgent,
        ...(publishedFilter !== 'all' && { published: publishedFilter === 'published' })
      })
      
      const response = await api.get(`/knowledge/?${params}`)
      const data = response.data
      setKnowledgeItems(data.knowledge || [])
    } catch (error) {
      console.error('Error fetching knowledge:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedAgent) {
      fetchKnowledge()
    }
  }, [selectedAgent, publishedFilter])

  // Handle drag and drop reordering
  const handleDragEnd = async (result) => {
    if (!result.destination) return

    const items = Array.from(knowledgeItems)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    // Update local state
    setKnowledgeItems(items)

    // Update order in database
    const updates = items.map((item, index) => ({
      id: item.id,
      order: index
    }))

    try {
      await api.put('/knowledge/reorder', { updates })
    } catch (error) {
      console.error('Error updating order:', error)
      // Revert on error
      fetchKnowledge()
    }
  }

  // Handle edit
  const handleEdit = (item) => {
    setEditDialog({ open: true, item: { ...item } })
  }

  const handleSaveEdit = async () => {
    try {
      await api.put(`/knowledge/${editDialog.item.id}`, editDialog.item)
      fetchKnowledge()
      setEditDialog({ open: false, item: null })
    } catch (error) {
      console.error('Error updating knowledge:', error)
    }
  }

  // Handle delete
  const handleDelete = (item) => {
    setDeleteDialog({ open: true, item })
  }

  const confirmDelete = async () => {
    try {
      await api.delete(`/knowledge/${deleteDialog.item.id}`)
      fetchKnowledge()
      setDeleteDialog({ open: false, item: null })
    } catch (error) {
      console.error('Error deleting knowledge:', error)
    }
  }

  // Handle publish toggle
  const handleTogglePublish = async (item) => {
    try {
      const updatedItem = {
        ...item,
        published: !item.published,
        accepted_by: !item.published ? 'admin@example.com' : null // In real app, use actual admin user
      }

      await api.put(`/knowledge/${item.id}`, updatedItem)
      fetchKnowledge()
    } catch (error) {
      console.error('Error updating publish status:', error)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <AdminPageWrapper title="Knowledge Management">
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" gap={2} mb={3}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Agent</InputLabel>
            <Select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              label="Agent"
            >
              {agents.map((agent) => (
                <MenuItem key={agent.name} value={agent.name}>
                  {agent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={publishedFilter}
              onChange={(e) => setPublishedFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="unpublished">Unpublished</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {knowledgeItems.length === 0 ? (
          <Alert severity="info">
            No knowledge items found for the selected criteria.
          </Alert>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="knowledge-list">
              {(provided) => (
                <TableContainer ref={provided.innerRef} {...provided.droppableProps}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={50}></TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Submitted By</TableCell>
                        <TableCell>Knowledge</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {knowledgeItems.map((item, index) => (
                        <Draggable key={item.id} draggableId={item.id} index={index}>
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                bgcolor: snapshot.isDragging ? 'action.hover' : 'inherit'
                              }}
                            >
                              <TableCell {...provided.dragHandleProps}>
                                <DragIcon color="action" />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.published ? 'Published' : 'Pending'}
                                  color={item.published ? 'success' : 'warning'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{item.user}</TableCell>
                              <TableCell>
                                <Box sx={{ maxWidth: 300 }}>
                                  <Typography variant="body2" noWrap>
                                    {item.modified_submission || item.original_submission}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{formatDate(item.created_at)}</TableCell>
                              <TableCell>
                                <Box display="flex" gap={1}>
                                  <Tooltip title={item.published ? 'Unpublish' : 'Publish'}>
                                    <IconButton
                                      onClick={() => handleTogglePublish(item)}
                                      color={item.published ? 'success' : 'warning'}
                                    >
                                      {item.published ? <PublishIcon /> : <UnPublishedIcon />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit">
                                    <IconButton onClick={() => handleEdit(item)}>
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton onClick={() => handleDelete(item)} color="error">
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, item: null })} maxWidth="md" fullWidth>
        <DialogTitle>Edit Knowledge Item</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Original Submission (Read-only)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editDialog.item?.original_submission || ''}
              disabled
              sx={{ mb: 3 }}
            />
            
            <Typography variant="subtitle2" gutterBottom>
              Modified Knowledge (Editable)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editDialog.item?.modified_submission || ''}
              onChange={(e) => setEditDialog({
                ...editDialog,
                item: { ...editDialog.item, modified_submission: e.target.value }
              })}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={editDialog.item?.published || false}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { 
                      ...editDialog.item, 
                      published: e.target.checked,
                      accepted_by: e.target.checked ? 'admin@example.com' : null
                    }
                  })}
                />
              }
              label="Published"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, item: null })}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle>Delete Knowledge Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this knowledge item? This action cannot be undone.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              {deleteDialog.item?.original_submission}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  )
}

export default AdminKnowledgePage